import { useNavigate, Link } from "react-router-dom";
import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import {
  ShieldCheck,
  Clock,
  Wrench,
  CreditCard,
  CarProfile,
  WhatsappLogo,
  Phone,
  CalendarBlank,
  ArrowRight,
  Sparkle,
  Lightning,
  Receipt,
  CheckCircle,
  Storefront,
  Armchair,
  CaretDown,
  Camera,
} from "@phosphor-icons/react";
import Header from "@/components/Header";
import SeoHead from "@/components/SeoHead";
import AutoglasFacts from "@/components/AutoglasFacts";
import LocationMap from "@/components/LocationMap";
import WerkstattGallery from "@/components/WerkstattGallery";
import ProcessShowcase from "@/components/ProcessShowcase";
import { SteinschlagScene, AUTOGLAS_STEPS } from "@/components/processScenes";
import { WORKSHOP_VIDEOS } from "@/data/workshopVideos";

const HERO_IMG = "/images/werkstatt/hero_taxi.jpg";
const MECHANIC_IMG = "/images/werkstatt/ferrari_sq.jpg";
const DAMAGE_IMG = "/images/werkstatt/werkstatt_innen.jpg";

const FEATURES = [
  {
    icon: Clock,
    title: "Schnelle Terminvergabe",
    text: "Oft schon innerhalb von 24 Stunden möglich.",
    details: "Nach Ihrer Schadenmeldung prüfen wir direkt, welcher Servus-Autoglas-Standort den schnellstmöglichen Termin für Sie anbieten kann. Bei vielen Glasschäden und Steinschlägen können wir bereits innerhalb von 24 Stunden einen Termin ermöglichen. So müssen Sie bei einer beschädigten Windschutzscheibe nicht unnötig lange warten. Unser Team organisiert die Reparatur oder den Scheibentausch schnell und unkompliziert an unseren Standorten in Mannheim, Ludwigshafen und München.",
  },
  {
    icon: Wrench,
    title: "Montage 3–4 Stunden",
    text: "Professionell und sicher repariert oder getauscht.",
    details: "Ein professioneller Austausch der Windschutzscheibe dauert bei vielen Fahrzeugen ungefähr 3–4 Stunden. Zuerst wird die beschädigte Autoscheibe fachgerecht entfernt. Anschließend wird die neue Scheibe vorbereitet, eingesetzt und sicher verklebt. Verfügt das Fahrzeug über Kameras, Sensoren oder Fahrerassistenzsysteme, prüfen wir auch, ob eine Kalibrierung notwendig ist. Zusätzlich beachten wir die erforderliche Stand- und Aushärtungszeit, damit das Fahrzeug anschließend wieder sicher genutzt werden kann.",
  },
  {
    icon: Receipt,
    title: "Direkt mit Versicherung",
    text: "Wir übernehmen die komplette Abwicklung.",
    details: "Bei einem versicherten Glasschaden übernehmen wir auf Wunsch die komplette Abwicklung mit Ihrer Kaskoversicherung. Sie müssen sich nicht selbst um komplizierte Formulare, Rechnungen oder die Kommunikation mit der Versicherung kümmern. Wir erfassen die notwendigen Schadendaten und kümmern uns um die weitere Abrechnung. Vor der Reparatur oder dem Austausch Ihrer Windschutzscheibe informieren wir Sie transparent darüber, welche Kosten von Ihrer Versicherung übernommen werden und ob eine Selbstbeteiligung anfällt.",
  },
  {
    icon: CreditCard,
    title: "Selbstbeteiligung flexibel",
    text: "Online (Apple Pay, PayPal …) oder vor Ort.",
    details: "Falls Ihr Versicherungsvertrag bei einem Glasschaden eine Selbstbeteiligung vorsieht, können Sie diese bei Servus Autoglas bequem und flexibel bezahlen. Je nach Auftrag stehen verschiedene Zahlungsmöglichkeiten zur Verfügung. Dazu gehören beispielsweise Apple Pay, PayPal sowie weitere Online-Zahlungsmöglichkeiten oder die Bezahlung direkt vor Ort. Vor Beginn der Arbeiten informieren wir Sie transparent über die anfallende Selbstbeteiligung und die verfügbaren Zahlungsarten.",
  },
];

const WHATSAPP_NUMBER = "4915222717582";
const PHONE_NUMBER = "+49 621 527429";
const PHONE_DISPLAY = "+49 621 527429";
const PHONE_TEL_LINK = "tel:+49621527429";

export default function Landing() {
  const navigate = useNavigate();
  const [callOpen, setCallOpen] = useState(false);
  const [openFeatures, setOpenFeatures] = useState([]);
  const [showMobileBar, setShowMobileBar] = useState(false);

  useEffect(() => {
    const onScroll = () => setShowMobileBar(window.scrollY > 600);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const toggleFeature = (i) =>
    setOpenFeatures((prev) => (prev.includes(i) ? prev.filter((x) => x !== i) : [...prev, i]));

  return (
    <div className="min-h-screen bg-slate-50">
      <SeoHead
        title="Servus Autoglas Ludwigshafen | Autoglas, Scheibentönung & Sattlerei"
        description="Servus Autoglas in Ludwigshafen: Steinschlag, Scheibentausch, Scheibentönung und Sattlerei — auch für Mannheim & Umgebung. Jetzt Leistungen entdecken und online Termin anfragen."
        canonical="https://servusautoglas.de/"
      />
      <Header />

      {/* HERO */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 bg-grid pointer-events-none opacity-60" />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-12 md:pt-20 pb-12 md:pb-20 relative">
          <div className="grid lg:grid-cols-2 gap-10 lg:gap-16 items-center">
            <div className="animate-fade-up">
              <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-blue-50 border border-blue-100 text-blue-700 text-xs font-semibold mb-6">
                <Sparkle size={14} weight="fill" />
                KI-gestützte Direktabwicklung
              </div>
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-slate-900 leading-[1.05]">
                Steinschlag oder
                <br />
                <span className="text-blue-600">Glasschaden?</span>
                <span className="block mt-4 text-lg sm:text-xl font-semibold text-slate-600">
                  Ihr Autoglas-Service für Ludwigshafen, Mannheim & Umgebung
                </span>
              </h1>
              <p className="mt-6 text-lg text-slate-600 leading-relaxed max-w-xl">
                Steinschlag, beschädigte Windschutzscheibe oder Fragen zur Versicherungsabwicklung?
                Stellen Sie Ihre Anfrage unkompliziert online, übermitteln Sie Ihre Fahrzeugdaten und
                prüfen Sie verfügbare Termine.
                <span className="block mt-1 font-medium text-slate-900">Wir unterstützen Sie bei der weiteren Abwicklung und erklären Ihnen die nächsten Schritte.</span>
              </p>

              <div className="mt-8 grid grid-cols-2 gap-3 max-w-xl">
                <Button
                  size="lg"
                  onClick={() => navigate("/schaden-melden")}
                  className="h-16 col-span-2 sm:col-span-2 bg-blue-600 hover:bg-blue-700 text-white text-base font-semibold rounded-xl shadow-sm"
                  data-testid="hero-report-claim-button"
                >
                  <Lightning size={20} weight="fill" className="mr-2" />
                  Schaden jetzt melden
                  <ArrowRight size={18} className="ml-2" />
                </Button>
                <Button
                  size="lg"
                  variant="outline"
                  onClick={() => navigate("/schnell-check")}
                  className="h-auto min-h-14 py-3 col-span-2 border-amber-300 bg-amber-50 hover:bg-amber-100 text-amber-900 rounded-xl font-semibold whitespace-normal"
                  data-testid="hero-schnellcheck-button"
                >
                  <Camera size={20} weight="fill" className="mr-2 shrink-0" />
                  NEU: Foto-Schnell-Check — sofort wissen, ob reparierbar
                </Button>
                <Button
                  size="lg"
                  variant="outline"
                  onClick={() => navigate("/schaden-melden")}
                  className="h-14 bg-white border-slate-200 hover:bg-slate-50 text-slate-800 rounded-xl"
                  data-testid="hero-book-appointment-button"
                >
                  <CalendarBlank size={18} className="mr-2" />
                  Termin buchen
                </Button>
                <Button
                  size="lg"
                  variant="outline"
                  onClick={() => navigate("/selbstzahler")}
                  className="h-14 bg-white border-slate-200 hover:bg-slate-50 text-slate-800 rounded-xl"
                  data-testid="hero-self-pay-button"
                >
                  <Receipt size={18} className="mr-2" />
                  Selbstzahler-Preis
                </Button>
                <a
                  href={`https://wa.me/${WHATSAPP_NUMBER}`}
                  target="_blank"
                  rel="noreferrer"
                  className="flex items-center justify-center gap-2 h-14 rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white font-medium transition-colors"
                  data-testid="hero-whatsapp-button"
                >
                  <WhatsappLogo size={20} weight="fill" />
                  WhatsApp
                </a>
                <button
                  type="button"
                  onClick={() => setCallOpen(true)}
                  className="flex items-center justify-center gap-2 h-14 rounded-xl bg-slate-900 hover:bg-slate-800 text-white font-medium transition-colors"
                  data-testid="hero-call-button"
                >
                  <Phone size={20} weight="fill" />
                  Anrufen
                </button>
              </div>

              <div className="mt-8 flex flex-wrap gap-x-6 gap-y-2 text-sm text-slate-600">
                {["Schnelle Termine", "Direkt mit Versicherung", "Ersatzfahrzeug möglich", "Transparente Preise"].map((t) => (
                  <div key={t} className="flex items-center gap-1.5">
                    <CheckCircle size={16} weight="fill" className="text-emerald-500" />
                    <span>{t}</span>
                  </div>
                ))}
              </div>
            </div>

            <div className="relative animate-fade-up">
              <div className="relative aspect-[4/5] rounded-3xl overflow-hidden bg-slate-900 shadow-2xl">
                <img src={HERO_IMG} alt="Echter Kundenfall: stark beschädigte Windschutzscheibe eines Taxis — Scheibentausch bei Servus Autoglas Ludwigshafen" className="w-full h-full object-cover opacity-95" />
                <div className="absolute inset-0 bg-gradient-to-t from-slate-900/70 via-transparent to-transparent" />
                <div className="absolute bottom-6 left-6 right-6 bg-white/95 backdrop-blur-xl rounded-2xl p-5 border border-white/40 shadow-xl">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 rounded-xl bg-blue-600 flex items-center justify-center flex-shrink-0">
                      <ShieldCheck size={26} weight="fill" color="white" />
                    </div>
                    <div>
                      <div className="font-semibold text-slate-900">Termin in 24 Stunden</div>
                      <div className="text-xs text-slate-500">Heute melden, morgen reparieren.</div>
                    </div>
                  </div>
                </div>
              </div>
              <div className="hidden md:block absolute -left-8 -bottom-8 w-44 h-44 rounded-2xl overflow-hidden border-4 border-white shadow-xl">
                <img src={MECHANIC_IMG} loading="lazy" alt="Ferrari in unserer Autoglas-Werkstatt — Premium-Service bei AutoGlas Erol in Ludwigshafen" className="w-full h-full object-cover" />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FEATURES */}
      <section className="py-16 md:py-24 bg-white border-y border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-2xl">
            <p className="text-xs font-semibold text-blue-600 uppercase tracking-widest">So einfach geht's</p>
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mt-3 tracking-tight">
              Vier Schritte. Keine Sorgen.
            </h2>
          </div>
          <div className="mt-12 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5 items-start">
            {FEATURES.map((f, i) => {
              const open = openFeatures.includes(i);
              return (
                <button
                  type="button"
                  key={i}
                  onClick={() => toggleFeature(i)}
                  aria-expanded={open}
                  aria-controls={`feature-details-${i}`}
                  className="w-full text-left bg-white border border-slate-200 rounded-2xl p-6 hover:-translate-y-1 hover:shadow-md transition-all duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2"
                  data-testid={`landing-feature-${i}`}
                >
                  <div className="w-12 h-12 rounded-xl bg-slate-900 flex items-center justify-center mb-4">
                    <f.icon size={24} weight="duotone" color="white" />
                  </div>
                  <div className="flex items-start justify-between gap-2">
                    <h3 className="font-bold text-slate-900 mb-1.5">{f.title}</h3>
                    <CaretDown
                      size={16}
                      className={`flex-shrink-0 mt-1 text-slate-400 transition-transform duration-300 ${open ? "rotate-180" : ""}`}
                      aria-hidden="true"
                    />
                  </div>
                  <p className="text-sm text-slate-600 leading-relaxed">{f.text}</p>
                  <div
                    id={`feature-details-${i}`}
                    className={`grid transition-[grid-template-rows] duration-300 ease-in-out ${open ? "grid-rows-[1fr]" : "grid-rows-[0fr]"}`}
                    aria-hidden={!open}
                  >
                    <div className="overflow-hidden">
                      <p className="pt-3 text-sm text-slate-500 leading-relaxed" data-testid={`landing-feature-details-${i}`}>
                        {f.details}
                      </p>
                    </div>
                  </div>
                </button>
              );
            })}
          </div>
        </div>
      </section>

      {/* SELF-PAY HIGHLIGHT */}
      <section className="py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="order-2 lg:order-1">
              <div className="aspect-[4/3] rounded-2xl overflow-hidden bg-slate-900">
                <img src={DAMAGE_IMG} loading="lazy" alt="Blick in unsere Autoglas-Werkstatt in Ludwigshafen — AutoGlas Erol GmbH (Servus Autoglas)" className="w-full h-full object-cover" />
              </div>
            </div>
            <div className="order-1 lg:order-2">
              <p className="text-xs font-semibold text-blue-600 uppercase tracking-widest">Keine Versicherung?</p>
              <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mt-3 tracking-tight">
                Sofortige Preis-Schätzung per KI
              </h2>
              <p className="mt-4 text-slate-600 leading-relaxed">
                Laden Sie einfach Ihren Fahrzeugschein hoch. Unsere KI erkennt Modell, Sensoren und Ausstattung
                und berechnet eine realistische Preisspanne in Sekunden.
              </p>
              <ul className="mt-6 space-y-2.5">
                {[
                  "Automatische Erkennung von Regensensor, Kamera, ADAS",
                  "Realistische Preisspanne inkl. Montage",
                  "Termin sofort mit 25 % Anzahlung sichern",
                  "Restbetrag bequem vor Ort zahlen",
                ].map((t) => (
                  <li key={t} className="flex items-start gap-2.5 text-slate-700">
                    <CheckCircle size={20} weight="fill" className="text-emerald-500 mt-0.5 flex-shrink-0" />
                    <span>{t}</span>
                  </li>
                ))}
              </ul>
              <Button
                size="lg"
                onClick={() => navigate("/selbstzahler")}
                className="mt-8 h-14 bg-slate-900 hover:bg-slate-800 text-white px-8 rounded-xl"
                data-testid="self-pay-cta"
              >
                <CarProfile size={20} className="mr-2" />
                Preis schätzen lassen
                <ArrowRight size={18} className="ml-2" />
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* AUTOGLAS FAKTEN (aufklappbar) */}
      <AutoglasFacts />

      {/* SO LÄUFT DIE STEINSCHLAGREPARATUR (animiert) + VIDEO */}
      <ProcessShowcase
        eyebrow="So arbeiten wir"
        title="Steinschlagreparatur in 30 Minuten — Schritt für Schritt"
        intro="Vom Steinschlag bis zur klaren Scheibe: So läuft die professionelle Reparatur mit UV-Spezialharz ab — verständlich animiert."
        steps={AUTOGLAS_STEPS}
        Scene={SteinschlagScene}
        accent="#2563eb"
        soft="#eff6ff"
        softBorder="#dbeafe"
        videos={[WORKSHOP_VIDEOS.autoglas]}
        testid="process-autoglas"
      />

      {/* PARTNER SHOP — Scheiben kaufen */}
      <section className="py-16 md:py-24 bg-white border-t border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="rounded-3xl bg-slate-900 overflow-hidden">
            <div className="grid lg:grid-cols-2 gap-8 items-center p-8 md:p-12">
              <div>
                <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-blue-500/15 border border-blue-400/20 text-blue-300 text-xs font-semibold mb-5">
                  <Storefront size={14} weight="fill" />
                  Unser Partner-Shop
                </div>
                <h2 className="text-3xl md:text-4xl font-bold text-white tracking-tight">
                  Frontscheibe lieber selbst kaufen?
                </h2>
                <p className="mt-4 text-slate-300 leading-relaxed">
                  Über unseren Partner-Shop <span className="font-semibold text-white">billigautoglas.de</span> bestellen
                  Sie Autoglas in Erstausrüsterqualität direkt online — Frontscheiben, Seitenscheiben und
                  Heckscheiben für alle gängigen Marken. Auf Wunsch übernehmen wir anschließend die Montage.
                </p>
                <ul className="mt-6 space-y-2.5">
                  {[
                    "Scheiben in OE/OEM-Qualität zu fairen Preisen",
                    "Riesige Auswahl für alle gängigen Fahrzeugmarken",
                    "Auf Wunsch professioneller Einbau bei Servus Autoglas",
                  ].map((t) => (
                    <li key={t} className="flex items-start gap-2.5 text-slate-200">
                      <CheckCircle size={20} weight="fill" className="text-emerald-400 mt-0.5 flex-shrink-0" />
                      <span>{t}</span>
                    </li>
                  ))}
                </ul>
                <a
                  href="https://www.billigautoglas.de"
                  target="_blank"
                  rel="noreferrer"
                  className="mt-8 inline-flex items-center justify-center gap-2 h-14 px-8 rounded-xl bg-blue-600 hover:bg-blue-500 text-white font-semibold transition-colors"
                  data-testid="partner-shop-button"
                >
                  <Storefront size={20} weight="fill" />
                  Zum Scheiben-Shop: billigautoglas.de
                  <ArrowRight size={18} />
                </a>
              </div>
              <div className="hidden lg:flex items-center justify-center">
                <div className="relative w-full max-w-md aspect-[4/3] rounded-2xl bg-gradient-to-br from-blue-600/20 to-slate-800 border border-slate-700 flex flex-col items-center justify-center p-8">
                  <Storefront size={72} weight="duotone" className="text-blue-400" />
                  <div className="mt-4 text-white font-bold text-xl">billigautoglas.de</div>
                  <div className="mt-1 text-slate-400 text-sm text-center">
                    Autoglas online kaufen — Montage bei uns in Ludwigshafen
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* SCHEIBENTÖNUNG — eigener Bereich (Pink) */}
      <section className="py-16 md:py-24 bg-white" id="scheibentoenung-teaser">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="rounded-3xl bg-slate-900 overflow-hidden ring-1 ring-pink-500/20">
            <div className="grid lg:grid-cols-2 gap-8 items-center p-8 md:p-12">
              <div>
                <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-pink-500/15 border border-pink-400/20 text-pink-300 text-xs font-semibold mb-5">
                  <Sparkle size={14} weight="fill" />
                  Neu · Scheibentönung zum Festpreis
                </div>
                <h2 className="text-3xl md:text-4xl font-bold text-white tracking-tight">
                  Autoscheiben tönen lassen
                </h2>
                <p className="mt-3 text-lg font-semibold text-pink-400">
                  Mehr Privatsphäre. Weniger Hitze. Hochwertiger Look.
                </p>
                <p className="mt-4 text-slate-300 leading-relaxed">
                  Lassen Sie die hinteren Scheiben Ihres Fahrzeugs professionell mit hochwertiger, zugelassener
                  Tönungsfolie folieren. Fahrzeug auswählen, verbindlichen Festpreis sofort sehen und den
                  Montagstermin direkt online buchen.
                </p>
                <ul className="mt-6 space-y-2.5">
                  {[
                    "Hochwertige Tönungsfolie mit ABG / Prüfnummer",
                    "Verbindlicher Festpreis ab 250 € inkl. Montage & MwSt.",
                    "Montagstermin direkt online buchen",
                  ].map((t) => (
                    <li key={t} className="flex items-start gap-2.5 text-slate-200">
                      <CheckCircle size={20} weight="fill" className="text-pink-400 mt-0.5 flex-shrink-0" />
                      <span>{t}</span>
                    </li>
                  ))}
                </ul>
                <Link
                  to="/scheibentoenung"
                  className="mt-8 inline-flex items-center justify-center gap-2 h-14 px-8 rounded-xl bg-pink-600 hover:bg-pink-500 text-white font-semibold transition-colors"
                  data-testid="tint-teaser-cta"
                >
                  Festpreis sichern &amp; Montagstermin buchen
                  <ArrowRight size={18} />
                </Link>
              </div>
              <div className="hidden lg:flex items-center justify-center">
                <div className="relative w-full max-w-md aspect-[4/3] rounded-2xl bg-gradient-to-br from-pink-600/20 to-slate-800 border border-slate-700 flex flex-col items-center justify-center p-8">
                  <CarProfile size={72} weight="duotone" className="text-pink-400" />
                  <div className="mt-4 text-white font-bold text-xl">ab 250 € Festpreis</div>
                  <div className="mt-1 text-slate-400 text-sm text-center">
                    Fahrzeug wählen — Preis sofort online · Termine montags
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* SATTLEREI — eigener Bereich (Grün) */}
      <section className="pb-16 md:pb-24 bg-white" id="sattlerei-teaser">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="rounded-3xl bg-slate-900 overflow-hidden ring-1 ring-[#55C96B]/25">
            <div className="grid lg:grid-cols-2 gap-8 items-center p-8 md:p-12">
              <div>
                <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-[#55C96B]/15 border border-[#55C96B]/25 text-[#7fdc92] text-xs font-semibold mb-5">
                  <Armchair size={14} weight="fill" />
                  Neu · Autosattlerei
                </div>
                <h2 className="text-3xl md:text-4xl font-bold text-white tracking-tight">
                  Autohimmel, Cabrioverdeck &amp; Cabrio-Heckscheibe
                </h2>
                <p className="mt-3 text-lg font-semibold text-[#7fdc92]">
                  Unsere Sattlerei in Ludwigshafen.
                </p>
                <p className="mt-4 text-slate-300 leading-relaxed">
                  Hängender Dachhimmel, gelöste Cabrio-Heckscheibe oder beschädigtes Verdeck?
                  Leistung auswählen, Preis online berechnen und Abgabetermin direkt buchen.
                </p>
                <ul className="mt-6 space-y-2.5">
                  {[
                    "Autohimmel erneuern ab 449 € inkl. MwSt.",
                    "Cabrio-Heckscheibe neu verkleben ab 399 €",
                    "Preis online berechnen & Termin direkt buchen",
                  ].map((t) => (
                    <li key={t} className="flex items-start gap-2.5 text-slate-200">
                      <CheckCircle size={20} weight="fill" className="text-[#55C96B] mt-0.5 flex-shrink-0" />
                      <span>{t}</span>
                    </li>
                  ))}
                </ul>
                <Link
                  to="/sattlerei"
                  className="mt-8 inline-flex items-center justify-center gap-2 h-14 px-8 rounded-xl bg-[#55C96B] hover:bg-[#249143] text-white font-semibold transition-colors"
                  data-testid="sattlerei-teaser-cta"
                >
                  Jetzt Preis berechnen
                  <ArrowRight size={18} />
                </Link>
              </div>
              <div className="hidden lg:flex items-center justify-center">
                <div className="relative w-full max-w-md aspect-[4/3] rounded-2xl bg-gradient-to-br from-[#55C96B]/20 to-slate-800 border border-slate-700 flex flex-col items-center justify-center p-8">
                  <Armchair size={72} weight="duotone" className="text-[#7fdc92]" />
                  <div className="mt-4 text-white font-bold text-xl">Sattlerei Ludwigshafen</div>
                  <div className="mt-1 text-slate-400 text-sm text-center">
                    Himmel · Verdeck · Heckscheibe — Termin online buchen
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* STATUS TRACKER CTA */}
      <section className="py-14 bg-slate-900 text-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 flex flex-col md:flex-row items-center justify-between gap-6">
          <div>
            <h3 className="text-2xl md:text-3xl font-bold tracking-tight">Schon einen Schaden gemeldet?</h3>
            <p className="text-slate-300 mt-1">Verfolgen Sie den Status Ihrer Reparatur in Echtzeit.</p>
          </div>
          <Button
            size="lg"
            onClick={() => navigate("/status")}
            className="h-14 bg-white hover:bg-slate-100 text-slate-900 px-8 rounded-xl font-semibold"
            data-testid="landing-track-status"
          >
            Status verfolgen
            <ArrowRight size={18} className="ml-2" />
          </Button>
        </div>
      </section>

      {/* SERVICEGEBIETE (SEO) */}
      <section className="py-14 bg-white border-t border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <p className="text-xs font-semibold text-blue-600 uppercase tracking-widest">Servicegebiete</p>
          <h2 className="text-2xl md:text-3xl font-bold text-slate-900 mt-3 tracking-tight">
            Autoglas-Service in Ihrer Stadt
          </h2>
          <p className="mt-3 text-slate-600 max-w-3xl leading-relaxed">
            Unsere Meisterwerkstatt in Ludwigshafen (Frankenthaler Str. 98) bedient Mannheim, Ludwigshafen und
            die gesamte Metropolregion Rhein-Neckar — und mit unserem digitalen Schadenassistenten jetzt auch
            München. Schaden online melden, Versicherung wird direkt abgewickelt.
          </p>
          <div className="mt-6 flex flex-wrap gap-2.5" data-testid="service-area-links">
            {[
              { to: "/autoglas-mannheim", label: "Autoglas Mannheim" },
              { to: "/autoglas-ludwigshafen", label: "Autoglas Ludwigshafen" },
              { to: "/autoglas-muenchen", label: "Autoglas München" },
              { to: "/steinschlagreparatur-mannheim", label: "Steinschlagreparatur Mannheim" },
              { to: "/steinschlagreparatur-ludwigshafen", label: "Steinschlagreparatur Ludwigshafen" },
              { to: "/steinschlagreparatur-muenchen", label: "Steinschlagreparatur München" },
              { to: "/windschutzscheibe-mannheim", label: "Windschutzscheibe Mannheim" },
              { to: "/windschutzscheibe-ludwigshafen", label: "Windschutzscheibe Ludwigshafen" },
              { to: "/windschutzscheibe-muenchen", label: "Windschutzscheibe München" },
              { to: "/scheibentoenung-mannheim", label: "Scheibentönung Mannheim" },
              { to: "/sattlerei-mannheim", label: "Autosattlerei Mannheim" },
              { to: "/sattlerei-heidelberg", label: "Autosattlerei Heidelberg" },
              { to: "/sattlerei-speyer", label: "Autosattlerei Speyer" },
              { to: "/standort/heidelberg", label: "Autoglas Heidelberg" },
              { to: "/standort/worms", label: "Autoglas Worms" },
              { to: "/standort/speyer", label: "Autoglas Speyer" },
              { to: "/standort/frankenthal", label: "Autoglas Frankenthal" },
            ].map((l) => (
              <Link
                key={l.to}
                to={l.to}
                className="px-4 py-2 rounded-full border border-slate-200 bg-slate-50 hover:bg-blue-50 hover:border-blue-200 hover:text-blue-700 text-sm text-slate-700 transition-colors"
              >
                {l.label}
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* WERKSTATT GALERIE (echte Fotos & Video) */}
      <WerkstattGallery />

      {/* WERKSTATT & ANFAHRT (Google Maps) */}
      <LocationMap />

      {/* FOOTER */}
      <footer className="bg-slate-900 text-slate-300 pt-16 pb-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-5 gap-8 mb-10">
            <div className="col-span-2 md:col-span-1">
              <div className="flex items-center gap-2 mb-4">
                <div className="w-9 h-9 rounded-lg bg-blue-600 flex items-center justify-center">
                  <ShieldCheck size={20} weight="fill" color="white" />
                </div>
                <div>
                  <div className="font-bold text-white">Servus Autoglas</div>
                  <div className="text-[10px] uppercase tracking-widest text-slate-500">Mannheim · Ludwigshafen · München</div>
                </div>
              </div>
              <p className="text-sm text-slate-400 leading-relaxed">
                Steinschlag, Frontscheibentausch & ADAS-Kalibrierung in Mannheim, Ludwigshafen, der Metropolregion Rhein-Neckar und München. Direktabwicklung mit allen großen Versicherungen.
              </p>
              <div className="mt-4 text-sm text-slate-400 space-y-1">
                <div>Frankenthaler Str. 98, 67059 Ludwigshafen</div>
                <div>Tel.: <a href="tel:+49621527429" className="hover:text-white">+49 621 527429</a></div>
                <div>Mo–Fr 09:00–18:00 · Sa 09:00–14:00</div>
              </div>
            </div>

            <div>
              <div className="text-xs font-semibold uppercase tracking-widest text-white mb-3">Leistungen</div>
              <ul className="space-y-2 text-sm">
                <li><Link to="/leistung/steinschlagreparatur" className="hover:text-white">Steinschlagreparatur</Link></li>
                <li><Link to="/leistung/windschutzscheibe-tauschen" className="hover:text-white">Windschutzscheibe tauschen</Link></li>
                <li><Link to="/leistung/seitenscheibe-ersetzen" className="hover:text-white">Seitenscheibe ersetzen</Link></li>
                <li><Link to="/leistung/heckscheibe-ersetzen" className="hover:text-white">Heckscheibe ersetzen</Link></li>
                <li><Link to="/leistung/adas-kalibrierung" className="hover:text-white">ADAS-Kalibrierung</Link></li>
                <li><Link to="/scheibentoenung" className="hover:text-white">Scheibentönung ab 250 €</Link></li>
                <li><Link to="/sattlerei" className="hover:text-white">Autosattlerei Ludwigshafen</Link></li>
                <li><Link to="/leistungen" className="hover:text-white font-medium">Alle Leistungen →</Link></li>
              </ul>
            </div>

            <div>
              <div className="text-xs font-semibold uppercase tracking-widest text-white mb-3">Standorte</div>
              <ul className="space-y-2 text-sm">
                <li><Link to="/standort/mannheim" className="hover:text-white">Autoglas Mannheim</Link></li>
                <li><Link to="/standort/ludwigshafen" className="hover:text-white">Autoglas Ludwigshafen</Link></li>
                <li><Link to="/autoglas-muenchen" className="hover:text-white">Autoglas München</Link></li>
                <li><Link to="/standort/heidelberg" className="hover:text-white">Autoglas Heidelberg</Link></li>
                <li><Link to="/standort/worms" className="hover:text-white">Autoglas Worms</Link></li>
                <li><Link to="/standort/speyer" className="hover:text-white">Autoglas Speyer</Link></li>
                <li><Link to="/standort/weinheim" className="hover:text-white">Autoglas Weinheim</Link></li>
                <li><Link to="/leistungen" className="hover:text-white font-medium">Alle Städte →</Link></li>
              </ul>
            </div>

            <div>
              <div className="text-xs font-semibold uppercase tracking-widest text-white mb-3">Sattlerei-Ratgeber</div>
              <ul className="space-y-2 text-sm">
                <li><Link to="/sattlerei/dachhimmel-neu-beziehen" className="hover:text-white">Dachhimmel neu beziehen</Link></li>
                <li><Link to="/sattlerei/fahrersitz-reparieren" className="hover:text-white">Fahrersitz reparieren</Link></li>
                <li><Link to="/sattlerei/sitzwangen-reparieren" className="hover:text-white">Sitzwangen reparieren</Link></li>
                <li><Link to="/sattlerei/cabrio-heckscheibe-kleben" className="hover:text-white">Cabrio-Heckscheibe kleben</Link></li>
                <li><Link to="/sattlerei/cabrioverdeck-reparieren" className="hover:text-white">Cabrioverdeck reparieren</Link></li>
                <li><Link to="/sattlerei/ledersitze-pflege" className="hover:text-white">Ledersitze pflegen</Link></li>
                <li><Link to="/sattlerei/innenraum-pflege" className="hover:text-white">Innenraum-Aufbereitung</Link></li>
              </ul>
            </div>

            <div>
              <div className="text-xs font-semibold uppercase tracking-widest text-white mb-3">Versicherung & Hilfe</div>
              <ul className="space-y-2 text-sm">
                <li><Link to="/schnell-check" className="hover:text-white">KI-Schnell-Check per Foto</Link></li>
                <li><Link to="/versicherungen" className="hover:text-white">Versicherungs-Direktabwicklung</Link></li>
                <li><Link to="/versicherung/huk-coburg" className="hover:text-white">HUK-Coburg Glasschaden</Link></li>
                <li><Link to="/versicherung/allianz" className="hover:text-white">Allianz Glasschaden</Link></li>
                <li><Link to="/versicherung/axa" className="hover:text-white">AXA Glasschaden</Link></li>
                <li><Link to="/faq" className="hover:text-white">FAQ — Häufige Fragen</Link></li>
                <li><Link to="/status" className="hover:text-white">Status verfolgen</Link></li>
              </ul>
            </div>
          </div>

          <div className="border-t border-slate-800 pt-6 flex flex-col md:flex-row justify-between items-start md:items-center gap-4 text-sm">
            <div className="text-slate-500">
              © {new Date().getFullYear()} AutoGlas Erol GmbH · Alle Rechte vorbehalten
            </div>
            <div className="flex flex-wrap gap-5">
              <Link to="/datenschutz" className="hover:text-white" data-testid="footer-datenschutz">Datenschutz</Link>
              <Link to="/agb" className="hover:text-white" data-testid="footer-agb">AGB</Link>
              <Link to="/widerruf" className="hover:text-white" data-testid="footer-widerruf">Widerruf</Link>
              <Link to="/impressum" className="hover:text-white" data-testid="footer-impressum">Impressum</Link>
              <Link to="/cookie" className="hover:text-white" data-testid="footer-cookie">Cookie</Link>
              <button onClick={() => navigate("/admin/login")} className="hover:text-white" data-testid="footer-admin-link">
                Admin
              </button>
            </div>
          </div>
        </div>
      </footer>
      {/* STICKY MOBILE CTA */}
      {showMobileBar && (
        <div
          className="fixed bottom-0 inset-x-0 md:hidden bg-white/95 backdrop-blur border-t border-slate-200 p-3 grid grid-cols-2 gap-3 z-40 animate-fade-up"
          data-testid="landing-sticky-cta"
        >
          <a
            href={PHONE_TEL_LINK}
            className="flex items-center justify-center gap-2 h-12 rounded-xl bg-slate-900 text-white font-semibold"
            data-testid="landing-sticky-call"
          >
            <Phone size={18} weight="fill" />
            Anrufen
          </a>
          <Button
            onClick={() => navigate("/schaden-melden")}
            className="h-12 rounded-xl bg-blue-600 hover:bg-blue-700 text-white font-semibold"
            data-testid="landing-sticky-report"
          >
            Schaden melden
          </Button>
        </div>
      )}
      {/* CALL DIALOG */}
      <Dialog open={callOpen} onOpenChange={setCallOpen}>
        <DialogContent className="sm:max-w-md" data-testid="call-dialog">
          <DialogHeader>
            <DialogTitle className="text-2xl">Rufen Sie uns an</DialogTitle>
            <DialogDescription>
              AutoGlas Erol GmbH — wir helfen Ihnen schnell und persönlich.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-5">
            <a
              href={PHONE_TEL_LINK}
              className="block w-full bg-blue-600 hover:bg-blue-700 text-white rounded-2xl p-6 text-center transition-colors"
              data-testid="call-dialog-number"
            >
              <div className="text-xs uppercase tracking-widest text-blue-100">Jetzt direkt anrufen</div>
              <div className="text-3xl sm:text-4xl font-bold mt-2 tracking-tight">{PHONE_DISPLAY}</div>
              <div className="flex items-center justify-center gap-2 mt-3 text-sm text-blue-100">
                <Phone size={16} weight="fill" /> Tippen zum Anrufen
              </div>
            </a>

            <div className="rounded-xl border border-slate-200 bg-slate-50 p-4">
              <div className="text-xs uppercase tracking-widest text-slate-500 font-semibold mb-2">Öffnungszeiten</div>
              <div className="text-sm text-slate-800 space-y-1">
                <div className="flex justify-between"><span>Montag – Freitag</span><span className="font-medium">09:00 – 18:00</span></div>
                <div className="flex justify-between"><span>Samstag</span><span className="font-medium">09:00 – 14:00</span></div>
                <div className="flex justify-between"><span>Sonntag</span><span className="font-medium text-slate-500">geschlossen</span></div>
              </div>
            </div>

            <div className="rounded-xl border border-emerald-200 bg-emerald-50 p-4">
              <div className="text-xs uppercase tracking-widest text-emerald-700 font-semibold mb-2">Außerhalb der Öffnungszeiten</div>
              <p className="text-sm text-emerald-900 leading-relaxed">
                Schreiben Sie uns per WhatsApp — wir melden uns schnellstmöglich zurück.
              </p>
              <a
                href={`https://wa.me/${WHATSAPP_NUMBER}`}
                target="_blank"
                rel="noreferrer"
                className="mt-3 inline-flex items-center gap-2 h-11 px-4 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-semibold"
                data-testid="call-dialog-whatsapp"
              >
                <WhatsappLogo size={16} weight="fill" /> WhatsApp öffnen
              </a>
            </div>

            <div className="text-center text-xs text-slate-500 pt-1">
              Frankenthaler Str. 98 · 67059 Ludwigshafen<br />
              info@billigautoglas.de
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
